import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Code2 } from 'lucide-react';
import { DialogModal } from '../DialogModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useEffect, useMemo, useState } from 'react';
// Carrier information removed - not critical for modal display

type ApplicationData = {
  clientPolicyApplicationId: string;
  policyApplicationFormId?: string;
  stepType: string;
  status: PolicyApplicationStep['status'];
  submissionData: any;
  visitedSubsections?: string[];
  submissionDateTime?: Date;
  stateOfResidenceAbbreviation: string | undefined;
};

type PreviousApplicationPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  previousApplicationData: ApplicationData[];
  selectedApplicationData: ApplicationData | null;
  onSelectApplication: (applicationId: string, formId: string) => void;
};

function formatLabel(str: string): string {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const PreviousApplicationPreviewModal = ({
  open,
  onClose,
  onAccept,
  previousApplicationData,
  selectedApplicationData,
  onSelectApplication,
}: PreviousApplicationPreviewModalProps) => {
  const [application, setApplication] = useState<string | null>(null);

  const options = useMemo(
    () =>
      previousApplicationData.map(app => {
        // Get carrier from Redux cache (forms are fetched via API)
        // For now, use a fallback since this is a preview modal
        const carrier = app?.policyApplicationFormId ? '' : ''; // TODO: Fetch carrier from API if needed

        return {
          value: `${app.clientPolicyApplicationId}_${app.policyApplicationFormId}`,
          label: `${app.status === 'completed' ? '✅' : '⏳'} ${carrier}(${app.stateOfResidenceAbbreviation}) - ${formatLabel(app.stepType)} - ${app.submissionDateTime?.toDateString()}`,
        };
      }),
    [previousApplicationData],
  );

  useEffect(() => {
    if (!application) return;

    const splitValue = application.split('_');

    onSelectApplication(splitValue[0], splitValue[1]);
  }, [application, previousApplicationData, selectedApplicationData, onSelectApplication]);

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title="Previous Form Submissions Data"
      subTitle="Select an application and review data before pre-filling"
    >
      <Select
        value={(application as string) || ''}
        onValueChange={newValue => setApplication(newValue)}
      >
        <SelectTrigger className="text-xs text-gray-900 [&>svg]:size-3">
          <SelectValue placeholder="Previous Application" />
        </SelectTrigger>
        <SelectContent className="max-w-[90vw]">
          {options.map(option => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="text-xs text-gray-900 [&>svg]:size-3"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Preview Data */}
      {selectedApplicationData && (
        <div className="mt-4 space-y-4">
          <Card className="rounded-xl border bg-gray-50 shadow-sm">
            <CardContent className="p-4">
              <div className="mb-2 flex items-center gap-2">
                <Code2 className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-700">Submission Data</span>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border bg-white p-2">
                <pre className="whitespace-pre-wrap text-xs text-gray-800 md:text-sm">
                  {JSON.stringify(selectedApplicationData.submissionData, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <p className="mt-2 text-sm md:text-base">
        <span className="font-medium text-gray-600 underline">
          Note: Only unfilled fields will be updated.
        </span>
      </p>

      <div className="mt-4 flex justify-end">
        <Button
          variant="default"
          type="button"
          onClick={onAccept}
          disabled={!selectedApplicationData}
        >
          Fill Form
        </Button>
      </div>
    </DialogModal>
  );
};
